/**
 * Google Analytics 4 Event Tracking Utility
 * Provides comprehensive tracking for CTAs, form submissions, and conversion funnels
 */

// Check if gtag is available
const isGAAvailable = () => {
  return typeof window !== "undefined" && typeof window.gtag === "function";
};

/**
 * Track CTA button clicks
 * @param {string} ctaLocation - Location of the CTA (e.g., 'hero', 'header', 'cta_section')
 * @param {string} ctaText - Text of the CTA button
 * @param {string} section - Section name where CTA is located
 */
export const trackCTAClick = (ctaLocation, ctaText, section = "") => {
  if (!isGAAvailable()) return;

  window.gtag("event", "cta_click", {
    event_category: "CTA",
    event_label: ctaText,
    cta_location: ctaLocation,
    cta_text: ctaText,
    section: section,
    value: 1,
  });
};

/**
 * Track form modal opens
 * @param {string} source - Where the form was opened from
 * @param {string} mode - 'application' or 'download'
 */
export const trackFormOpen = (source, mode = "application") => {
  if (!isGAAvailable()) return;

  window.gtag("event", "form_open", {
    event_category: "Form",
    event_label: `Form Opened - ${mode}`,
    form_mode: mode,
    form_source: source,
    funnel_step: "form_viewed",
    value: 1,
  });
};

/**
 * Track form field interactions
 * @param {string} fieldName - Name of the field
 * @param {string} action - 'focus', 'blur', 'change'
 */
export const trackFormFieldInteraction = (fieldName, action) => {
  if (!isGAAvailable()) return;

  window.gtag("event", "form_field_interaction", {
    event_category: "Form",
    event_label: `${fieldName} - ${action}`,
    field_name: fieldName,
    interaction_type: action,
    value: 1,
  });
};

/**
 * Track form submission start
 * @param {string} mode - 'application' or 'download'
 */
export const trackFormSubmitStart = (mode = "application") => {
  if (!isGAAvailable()) return;

  window.gtag("event", "form_submit_start", {
    event_category: "Form",
    event_label: `Form Submit Started - ${mode}`,
    form_mode: mode,
    funnel_step: "form_submission_started",
    value: 1,
  });
};

/**
 * Track successful form submission
 * @param {string} mode - 'application' or 'download'
 * @param {object} formData - Form data (optional, for enhanced tracking)
 */
export const trackFormSubmitSuccess = (mode = "application", formData = {}) => {
  if (!isGAAvailable()) return;

  // Track as conversion event
  window.gtag("event", "conversion", {
    send_to: "G-LQ5LT5YEW2",
    event_category: "Conversion",
    event_label: mode === "download" ? "PDF Download" : "Application Submitted",
    form_mode: mode,
    funnel_step: "form_submitted",
    value: mode === "download" ? 10 : 100, // Higher value for application
    currency: "INR",
  });

  // Track as form submission event
  window.gtag("event", "form_submit_success", {
    event_category: "Form",
    event_label: `Form Submitted Successfully - ${mode}`,
    form_mode: mode,
    funnel_step: "form_submitted",
    programme: formData.programme || "not_selected",
    value: mode === "download" ? 10 : 100,
  });
};

/**
 * Track form submission error
 * @param {string} mode - 'application' or 'download'
 * @param {string} errorMessage - Error message
 */
export const trackFormSubmitError = (
  mode = "application",
  errorMessage = ""
) => {
  if (!isGAAvailable()) return;

  window.gtag("event", "form_submit_error", {
    event_category: "Form",
    event_label: `Form Submit Error - ${mode}`,
    form_mode: mode,
    error_message: errorMessage,
    funnel_step: "form_submission_failed",
    value: 0,
  });
};

/**
 * Track PDF download
 */
export const trackPDFDownload = () => {
  if (!isGAAvailable()) return;

  window.gtag("event", "file_download", {
    event_category: "Download",
    event_label: "Course Details PDF",
    file_name: "TFBC-course-details.pdf",
    file_extension: "pdf",
    value: 10,
  });
};

/**
 * Track page views (for SPA routing)
 * @param {string} pagePath - Path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (!isGAAvailable()) return;

  window.gtag("config", "G-LQ5LT5YEW2", {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * Track scroll depth
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 */
export const trackScrollDepth = (percentage) => {
  if (!isGAAvailable()) return;

  window.gtag("event", "scroll", {
    event_category: "Engagement",
    event_label: `Scrolled ${percentage}%`,
    scroll_depth: percentage,
    value: percentage,
  });
};

/**
 * Track time on page
 * @param {number} seconds - Time spent on page in seconds
 */
export const trackTimeOnPage = (seconds) => {
  if (!isGAAvailable()) return;

  window.gtag("event", "timing_complete", {
    name: "time_on_page",
    value: seconds,
    event_category: "Engagement",
  });
};

/**
 * Track section views
 * @param {string} sectionName - Name of the section
 */
export const trackSectionView = (sectionName) => {
  if (!isGAAvailable()) return;

  window.gtag("event", "section_view", {
    event_category: "Engagement",
    event_label: sectionName,
    section_name: sectionName,
    value: 1,
  });
};

/**
 * Track external link clicks
 * @param {string} url - URL of the external link
 * @param {string} linkText - Text of the link
 */
export const trackExternalLink = (url, linkText = "") => {
  if (!isGAAvailable()) return;

  window.gtag("event", "click", {
    event_category: "Outbound",
    event_label: linkText || url,
    outbound_url: url,
    value: 1,
  });
};

/**
 * Track video interactions (for YouTube section)
 * @param {string} videoId - YouTube video ID
 * @param {string} action - 'play', 'pause', 'complete'
 */
export const trackVideoInteraction = (videoId, action) => {
  if (!isGAAvailable()) return;

  window.gtag("event", "video_interaction", {
    event_category: "Video",
    event_label: `Video ${action}`,
    video_id: videoId,
    video_action: action,
    value: 1,
  });
};

/**
 * Track Instagram reel views
 * @param {string} reelId - Instagram reel ID
 */
export const trackReelView = (reelId) => {
  if (!isGAAvailable()) return;

  window.gtag("event", "reel_view", {
    event_category: "Social Media",
    event_label: "Instagram Reel Viewed",
    reel_id: reelId,
    value: 1,
  });
};

